import $ from "jquery";
import {gsap} from "gsap/all";

// The "?" pill over the experts' list. Pressing it stretches the pill to the
// right to reveal the instructions, and the round button slides over to the
// other end, where it becomes the close button.
// The shape is two round caps joined by straight lines (like PillButton).

export class InstructionsPill {

  // Start and end shapes
  static CLOSED = {width: 164, leftRadius: 63.5, rightRadius: 38.5, buttonScale: 1};
  static OPEN = {width: 960, leftRadius: 48, rightRadius: 81.5, buttonScale: 0.7925};

  constructor($el) {
    this.$el = $el;
    this.path = $el.find(".shape path")[0];
    this.$toggle = $el.find(".toggle");
    this.$questionIcon = $el.find(".icon-question");
    this.$closeIcon = $el.find(".icon-close");
    this.$content = $el.find(".content");

    // Shape height, the caps are centered on it
    this.height = parseFloat($el.find(".shape").attr("height"));

    this.state = {progress: 0};
    this.isOpen = false;
    this.timeline = null;

    gsap.set(this.$content, {
      autoAlpha: 0,
      x: -20
    });
    this.hide();
    this.draw();
  }

  // Hidden until the tutorial tool tips are done and the game can be played
  show() {
    gsap.to(this.$el, {
      autoAlpha: 1,
      duration: 0.3,
      ease: "power1.out"
    });
  }

  // Snaps shut and hides (reset, idle)
  hide() {
    this.close(true);
    gsap.killTweensOf(this.$el);
    gsap.set(this.$el, {autoAlpha: 0});
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.$el.addClass("is-open");

    this.timeline?.kill();
    this.timeline = gsap.timeline();

    this.timeline.to(this.state, {
      progress: 1,
      duration: 0.7,
      ease: "power3.inOut",
      onUpdate: () => this.draw()
    });

    this.timeline.to(this.$content, {
      autoAlpha: 1,
      x: 0,
      duration: 0.3,
      ease: "power1.out"
    }, "-=0.2");
  }

  // Pass true to snap it shut without animating.
  close(immediate = false) {
    if (!this.isOpen && !immediate) return;
    this.isOpen = false;
    this.$el.removeClass("is-open");
    this.timeline?.kill();

    if (immediate) {
      this.state.progress = 0;
      gsap.set(this.$content, {
        autoAlpha: 0,
        x: -20
      });
      this.draw();
      return;
    }

    this.timeline = gsap.timeline();

    this.timeline.to(this.$content, {
      autoAlpha: 0,
      x: -20,
      duration: 0.2,
      ease: "power1.in"
    });

    this.timeline.to(this.state, {
      progress: 0,
      duration: 0.6,
      ease: "power3.inOut",
      onUpdate: () => this.draw()
    });
  }

  draw() {
    const p = this.state.progress;
    const lerp = (key) => gsap.utils.interpolate(InstructionsPill.CLOSED[key], InstructionsPill.OPEN[key], p);
    const width = lerp("width");
    const leftRadius = lerp("leftRadius");
    const rightRadius = lerp("rightRadius");
    const cy = this.height / 2;

    this.path.setAttribute("d", InstructionsPill.buildPath(width, cy, leftRadius, rightRadius));

    // The button rides from the left cap's center to the right cap's center
    const buttonX = gsap.utils.interpolate(leftRadius, width - rightRadius, p);
    gsap.set(this.$toggle, {
      x: buttonX,
      xPercent: -50,
      yPercent: -50,
      scale: lerp("buttonScale")
    });

    // "?" fades out in the first half, the X fades in during the second
    gsap.set(this.$questionIcon, {opacity: gsap.utils.clamp(0, 1, 1 - p * 2)});
    gsap.set(this.$closeIcon, {opacity: gsap.utils.clamp(0, 1, p * 2 - 1)});
  }

  // Two round caps of any size, both centered on cy, joined by straight lines.
  // Starts at x = 0 and ends at x = width.
  static buildPath(width, cy, leftRadius, rightRadius) {
    const cxLeft = leftRadius;
    const cxRight = width - rightRadius;
    const dx = cxRight - cxLeft;

    // Direction of the sloped edges, they tilt toward the smaller cap
    let nx = dx > 0 ? -(rightRadius - leftRadius) / dx : 0;
    if (!isFinite(nx) || Math.abs(nx) >= 1) nx = 0;
    const ny = Math.sqrt(1 - nx * nx);

    // Points where the straight edges meet each cap
    const luX = cxLeft + leftRadius * nx;
    const luY = cy - leftRadius * ny;
    const llY = cy + leftRadius * ny;
    const ruX = cxRight + rightRadius * nx;
    const ruY = cy - rightRadius * ny;
    const rlY = cy + rightRadius * ny;

    // The bigger cap wraps around more than half a circle
    const rightLargeArc = rightRadius >= leftRadius ? 1 : 0;
    const leftLargeArc = leftRadius > rightRadius ? 1 : 0;

    const n = (v) => Math.round(v * 100) / 100;

    return [
      `M ${n(luX)} ${n(luY)}`,
      `L ${n(ruX)} ${n(ruY)}`,
      `A ${n(rightRadius)} ${n(rightRadius)} 0 ${rightLargeArc} 1 ${n(ruX)} ${n(rlY)}`, // right cap
      `L ${n(luX)} ${n(llY)}`,
      `A ${n(leftRadius)} ${n(leftRadius)} 0 ${leftLargeArc} 1 ${n(luX)} ${n(luY)}`, // left cap
      "Z",
    ].join(" ");
  }
}
